import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Injector,
  Input,
  OnInit,
  Optional,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DialogRemoveComponent } from '@brunsker/libs/core/ui/table-actions/src/lib/components/remove/dialog-remove/dialog-remove.component';
import { DescontoService } from '@brunsker/libs/core/ui/table-actions/src/lib/services/desconto/desconto.service';
import { UtilsBsHotToastService } from '@brunsker/libs/core/utils/hot-toast/src';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@Component({
  selector: 'bs-input-incrementor',
  templateUrl: './input-incrementor.component.html',
  styleUrls: ['./input-incrementor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: UiInputIncrementorComponent,
      multi: true,
    },
  ],
})
@UntilDestroy()
export class UiInputIncrementorComponent
  implements OnInit, ControlValueAccessor {
  @Input() removeRodape!: boolean;
  @Input() maxIncrementor!: number;
  @Input() hasMultiple!: string;
  @Input() removeProd: boolean = true;
  @HostBinding('style.--backgroundColor') @Input() backgroundColor!: string;
  @HostBinding('style.--backgroundColorDark')
  @Input()
  backgroundColorDark!: string;
  incrementControl = new FormControl<number>();
  value!: number;
  ngControl!: NgControl | null;
  onChange?: (value: number) => void;
  onTouched?: () => void;
  controlRodape: boolean = false;

  constructor(
    private readonly injector: Injector,
    @Optional() private readonly controlContainer: ControlContainer,
    readonly descontoService: DescontoService,
    readonly toast: UtilsBsHotToastService,
    readonly matDialog: MatDialog
  ) { }

  writeValue(value: number): void {
    if (this.maxValue()) return;
    if (this.hasMultiple) {
      const fractionValue = +value / +this.hasMultiple;
      const fixedFraction = Math.floor(fractionValue);

      const newValue = +(fixedFraction * +this.hasMultiple).toFixed(2);

      if (value > newValue) {
        const nextValue = +(newValue + +this.hasMultiple).toFixed(2);
        this.value = nextValue;
        this.incrementControl.setValue(nextValue, {
          emitEvent: false,
        });

        return;
      }

      this.value = +newValue;
      this.incrementControl.setValue(+newValue, {
        emitEvent: false,
      });

      if (this.hasMultiple && value === 1 && +this.hasMultiple === 1) {
        this.value = +this.hasMultiple;
        this.incrementControl.setValue(+this.hasMultiple, {
          emitEvent: false,
        });
        return;
      }

      return;
    }

    this.value = value;
    this.incrementControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: any) => void): void {
    if (this.maxValue()) return;
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    if (this.maxValue()) return;
    this.onTouched = fn;
  }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
    if (this.removeRodape) {
      this.descontoService.descontoRodapeValue$.setValue({
        value: 0,
        prefix: '%',
      });
    }
    this.incrementControl.valueChanges
      .pipe(
        untilDestroyed(this)
        // filter((value) => value == +'' || value >= 1)
      )
      .subscribe((value) => {
        if (value <= 0 && this.removeProd) {
          this.matDialog
            .open(DialogRemoveComponent, {
              width: 'min(500px, 100%)',
              autoFocus: true,
              data: {
                content: {
                  h1: 'Remover produto',
                  p: 'Deseja realmente remover o produto?',
                  button: 'Remover do carrinho',
                },
              },
            })
            .afterClosed()
            .subscribe((res) => {
              if (res) {
                this.onChange?.apply(this, [res]);
                this.writeValue(res);
              }
              else {
                this.incrementControl.setValue(1, { emitEvent: false });
                this.writeValue(1);
                this.onChange?.apply(this, [1]);
                return;
              }
            });
          return;
        }
        if (this.maxValue()) return;
        if (value >= this.maxIncrementor) {
          this.incrementControl.setValue(this.maxIncrementor, {
            emitEvent: false,
          });
          this.writeValue(this.maxIncrementor);
          this.onChange?.apply(this, [this.maxIncrementor]);
          return;
        }

        this.onChange?.apply(this, [value]);
        this.writeValue(value);
      });

    this.ngControl = this.injector.get(NgControl, null);

    if (this.ngControl && this.ngControl.control) {
      this.incrementControl.validator = this.ngControl.control.validator;
    }

    const hasControl = this.controlContainer && this.controlContainer.control;

    if (!hasControl && this.controlContainer) {
      const formGroup = this.controlContainer.control as FormGroup<any>;
      const key = this.ngControl!.name as keyof FormGroup<any>;
      const control = formGroup.get(key) as AbstractControl;
      this.incrementControl.validator = control.validator;
    }
  }

  maxValue(): boolean {
    if (this.incrementControl.value > 10000) {
      this.incrementControl.setValue(10000);
      return true;
    }
    return false;
  }

  validationRodape(boolean?: boolean) {
    const validation =
      this.descontoService.descontoRodapeValue$?.value?.value != 0;
    if (validation) {
      if (boolean)
        this.toast.showWarning(
          'Remova o desconto de rodapé para poder fazer as alterações necessárias.'
        );
      return true;
    }
    return false;
  }

  increment() {
    if (this.validationRodape(true)) return;
    if (this.maxValue()) return;
    if (this.hasMultiple)
      return this.incrementControl.setValue(
        this.incrementControl.value + +this.hasMultiple
      );

    this.incrementControl.setValue(this.incrementControl.value + 1);
  }
  decrement() {
    if (this.validationRodape(true)) return;
    if (
      this.incrementControl.value < 0 ||
      this.incrementControl.value - 1 < 0
    ) {
      this.incrementControl.setValue(0);
      return;
    }

    if (this.hasMultiple) {
      const subtractMultiple = this.incrementControl.value - +this.hasMultiple;
      const fixedMultiple =
        subtractMultiple > 0 ? subtractMultiple.toFixed(2) : 0;

      return this.incrementControl.setValue(+fixedMultiple);
    }

    this.incrementControl.setValue(this.incrementControl.value - 1);
  }
}
